import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { publicAPI, tryoutAPI, purchaseAPI, uploadAPI } from '@/lib/api';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  BookOpen, FileText, Clock, ArrowRight, Search, 
  GraduationCap, Target, Sparkles, Users, CheckCircle,
  Loader2, AlertTriangle, Building2, Copy, Upload, Banknote, Lock, QrCode
} from 'lucide-react';
import { toast } from 'sonner';

// Program yang konsisten di seluruh aplikasi
const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA',
  'SMA Favorit'
];

export default function TryoutsPage() {
  const { user } = useAuth();
  const [tryouts, setTryouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [search, setSearch] = useState('');
  
  // Payment dialog state
  const [paymentDialog, setPaymentDialog] = useState({ open: false, tryout: null });
  const [paymentForm, setPaymentForm] = useState({
    bank_name: '',
    sender_name: '',
    payment_proof_url: ''
  });
  const [uploadingProof, setUploadingProof] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedProgram]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await publicAPI.getTryouts(selectedProgram !== 'all' ? selectedProgram : null);
      setTryouts(res.data || []);
    } catch (err) {
      console.error('Failed to load tryouts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle payment needed
  const handlePaymentNeeded = (tryout) => {
    setPaymentDialog({ open: true, tryout });
    setPaymentForm({ bank_name: '', sender_name: '', payment_proof_url: '' });
  };

  // Handle payment proof upload
  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    setUploadingProof(true);
    try {
      const response = await uploadAPI.uploadPaymentProof(file);
      setPaymentForm(prev => ({ ...prev, payment_proof_url: response.data.url }));
      toast.success('Bukti pembayaran berhasil diunggah');
    } catch (err) {
      toast.error('Gagal mengunggah bukti pembayaran');
    } finally {
      setUploadingProof(false);
    }
  };

  // Submit payment
  const submitPayment = async () => {
    if (!paymentDialog.tryout || !user) return;
    if (!paymentForm.payment_proof_url) {
      toast.error('Silakan upload bukti pembayaran');
      return;
    }
    if (!paymentForm.bank_name.trim() || !paymentForm.sender_name.trim()) {
      toast.error('Silakan lengkapi data pembayaran');
      return;
    }
    setSubmittingPayment(true);
    try {
      await purchaseAPI.request({
        item_type: 'tryout',
        item_id: paymentDialog.tryout.id,
        payment_proof: paymentForm.payment_proof_url,
        payment_method: 'manual_transfer',
        bank_name: paymentForm.bank_name,
        sender_name: paymentForm.sender_name,
        transfer_amount: paymentDialog.tryout.price
      });
      toast.success('Permintaan pembelian berhasil dikirim! Menunggu konfirmasi admin.');
      setPaymentDialog({ open: false, tryout: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim permintaan');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const filteredTryouts = tryouts.filter(to => {
    if (search && !to.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group tryouts by program for better display
  const groupedByProgram = filteredTryouts.reduce((acc, to) => {
    const program = to.category || 'Lainnya';
    if (!acc[program]) acc[program] = [];
    acc[program].push(to);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="tryouts-page">
      {/* Header with Mobile Navigation */}
      <Header />

      {/* Hero with Gradient */}
      <section className="relative bg-gradient-to-r from-[#4472C4] via-[#5a8dd6] to-[#4472C4] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 mb-4 px-4 py-1.5">
            <Sparkles className="w-3 h-3 mr-1.5" />
            Try Out Online Berkualitas
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Latihan Soal untuk <br className="hidden md:block" />
            <span className="text-yellow-300">Sukses Ujianmu</span>
          </h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto mb-8">
            Ribuan soal dengan pembahasan lengkap, timer otomatis, dan sistem scoring akurat
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-300">{tryouts.length}+</div>
              <div className="text-sm opacity-80">Try Out Tersedia</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-300">1000+</div>
              <div className="text-sm opacity-80">Soal Berkualitas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-300">{PROGRAMS.length}</div>
              <div className="text-sm opacity-80">Program Ujian</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 px-4 bg-white border-b border-slate-100 sticky top-[57px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-4 items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cari try out..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedProgram} onValueChange={setSelectedProgram}>
              <SelectTrigger className="w-52 bg-slate-50 border-slate-200" data-testid="program-filter">
                <SelectValue placeholder="Semua Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Program</SelectItem>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Target className="w-4 h-4" />
            <span>Menampilkan <strong className="text-slate-700">{filteredTryouts.length}</strong> try out</span>
          </div>
        </div>
      </section>

      {/* Tryouts Content */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-500 font-medium">Memuat try out...</p>
            </div>
          ) : filteredTryouts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-slate-300" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Tidak Ada Try Out</h3>
              <p className="text-slate-500 mb-6">Coba ubah filter pencarian atau pilih program lain</p>
              <Button onClick={() => { setSelectedProgram('all'); setSearch(''); }} variant="outline">
                Reset Filter
              </Button>
            </div>
          ) : selectedProgram === 'all' ? (
            // Show grouped by program when "Semua Program" selected
            <div className="space-y-12">
              {Object.entries(groupedByProgram).map(([program, programTryouts]) => (
                <div key={program}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#4472C4] to-[#5a8dd6] rounded-xl flex items-center justify-center shadow-md">
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">{program}</h2>
                      <p className="text-sm text-slate-500">{programTryouts.length} try out tersedia</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {programTryouts.map((to) => (
                      <TryoutCard key={to.id} tryout={to} user={user} onPaymentNeeded={handlePaymentNeeded} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Show flat list when specific program selected
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredTryouts.map((to) => (
                <TryoutCard key={to.id} tryout={to} user={user} onPaymentNeeded={handlePaymentNeeded} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF6B35] to-[#ff8555]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Siap Menghadapi Ujian?</h2>
          <p className="text-lg opacity-90 mb-8">
            Daftar sekarang dan akses semua try out untuk persiapan terbaikmu
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-white text-[#FF6B35] hover:bg-slate-100 px-8 py-6 text-lg rounded-xl shadow-lg">
                Daftar Gratis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/packages">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl">
                Lihat Paket
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Payment Dialog for Paid Tryouts - Compact & Scrollable */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => !open && setPaymentDialog({ open: false, tryout: null })}>
        <DialogContent className="max-w-xs max-h-[90vh] flex flex-col">
          <DialogHeader className="pb-1 flex-shrink-0">
            <DialogTitle className="text-sm flex items-center gap-2">
              <Banknote className="w-4 h-4 text-amber-600" />
              Bayar Try Out
            </DialogTitle>
          </DialogHeader>
          
          {paymentDialog.tryout && (
            <div className="space-y-2 text-xs overflow-y-auto flex-1 pr-1">
              {/* Tryout Info */}
              <div className="bg-amber-50 p-2 rounded-lg">
                <h3 className="font-semibold text-slate-900 text-sm truncate">{paymentDialog.tryout.title}</h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-slate-500">Total</span>
                  <span className="text-base font-bold text-amber-600">
                    {formatPrice(paymentDialog.tryout.price)}
                  </span>
                </div>
              </div>

              {/* Bank & QRIS - Stacked on small mobile, side by side on larger */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {/* Bank Info - Compact with overflow handling */}
                <div className="bg-blue-50 border border-blue-200 rounded p-1.5">
                  <div className="flex items-center gap-1 text-blue-800 font-medium mb-1 text-[9px]">
                    <Building2 className="w-2.5 h-2.5 shrink-0" />
                    <span>Transfer:</span>
                  </div>
                  <div className="space-y-0.5 text-[8px]">
                    {[
                      { bank: 'BCA', no: '1950947191' },
                      { bank: 'BNI', no: '0340100950' },
                      { bank: 'Mandiri', no: '106-00-1301771-3' },
                      { bank: 'BRI', no: '0336-01-092728-50-2' },
                      { bank: 'E-Wallet', no: '081262017001' },
                    ].map((acc) => (
                      <div key={acc.bank} className="flex items-center justify-between bg-white px-1 py-0.5 rounded overflow-hidden">
                        <span className="text-slate-600 shrink-0">{acc.bank}</span>
                        <button 
                          onClick={() => { navigator.clipboard.writeText(acc.no); toast.success('Disalin!'); }}
                          className="text-blue-700 hover:underline font-mono text-[7px] truncate ml-1"
                        >
                          {acc.no}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-[7px] text-blue-800 font-medium mt-1 pt-1 border-t border-blue-200">
                    a.n. Togi P. S. Pasaribu
                  </p>
                </div>

                {/* QRIS Payment - Smaller */}
                <div className="bg-emerald-50 border border-emerald-200 rounded p-1.5">
                  <div className="flex items-center gap-1 text-emerald-800 font-medium mb-1 text-[9px]">
                    <QrCode className="w-2.5 h-2.5 shrink-0" />
                    <span>QRIS</span>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/qris-mytentor.png" 
                      alt="QRIS MyTentor" 
                      className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                    />
                  </div>
                  <p className="text-center text-[7px] text-emerald-700">
                    GoPay, OVO, DANA
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label className="text-[10px]">Bank *</Label>
                  <Input
                    placeholder="BCA..."
                    value={paymentForm.bank_name}
                    onChange={(e) => setPaymentForm(p => ({ ...p, bank_name: e.target.value }))}
                    className="h-6 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Nama *</Label>
                  <Input
                    placeholder="Nama"
                    value={paymentForm.sender_name}
                    onChange={(e) => setPaymentForm(p => ({ ...p, sender_name: e.target.value }))}
                    className="h-6 text-xs"
                  />
                </div>
              </div>

              {/* Upload Proof */}
              <div className="border border-dashed border-slate-300 rounded p-1.5 text-center">
                {paymentForm.payment_proof_url ? (
                  <div className="flex items-center justify-center gap-2">
                    <img src={paymentForm.payment_proof_url} alt="Bukti" className="h-10 rounded" />
                    <button onClick={() => setPaymentForm(p => ({ ...p, payment_proof_url: '' }))} className="text-[10px] text-red-500">Hapus</button>
                  </div>
                ) : (
                  <label className="cursor-pointer flex items-center justify-center gap-1 py-1">
                    <input type="file" accept="image/*" onChange={handlePaymentProofUpload} className="hidden" disabled={uploadingProof} />
                    {uploadingProof ? <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> : (
                      <>
                        <Upload className="w-4 h-4 text-amber-600" />
                        <span className="text-[10px] text-slate-500">Upload bukti</span>
                      </>
                    )}
                  </label>
                )}
              </div>

              <p className="text-[9px] text-amber-700 bg-amber-50 p-1 rounded flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" />
                Admin verifikasi maks 1x24 jam
              </p>
            </div>
          )}

          <DialogFooter className="gap-1 pt-2 flex-shrink-0 border-t mt-2">
            <Button variant="outline" size="sm" onClick={() => setPaymentDialog({ open: false, tryout: null })} className="h-7 text-xs">
              Batal
            </Button>
            <Button size="sm" onClick={submitPayment} disabled={submittingPayment || !paymentForm.payment_proof_url} className="bg-amber-600 hover:bg-amber-700 h-7 text-xs">
              {submittingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Kirim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
}

// Separate component for Tryout Card - Compact Design with Payment Check
function TryoutCard({ tryout, user, onPaymentNeeded }) {
  const navigate = useNavigate();
  const to = tryout;
  const isFree = to.price === 0;
  const [checking, setChecking] = useState(false);

  const handleStartTryout = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    // For free tryouts, go directly
    if (isFree) {
      navigate(`/exam/${to.id}`);
      return;
    }

    // For paid tryouts, check access first
    setChecking(true);
    try {
      const res = await tryoutAPI.checkAccess(to.id);
      if (res.data.has_access) {
        navigate(`/exam/${to.id}`);
      } else if (res.data.reason === 'pending_confirmation') {
        toast.info('Pembayaran Anda sedang menunggu konfirmasi admin');
      } else {
        // Need to pay - show payment dialog
        onPaymentNeeded(to);
      }
    } catch (err) {
      console.error('Check access error:', err);
      // If error, assume need payment
      onPaymentNeeded(to);
    } finally {
      setChecking(false);
    }
  };
  
  return (
    <Card 
      className={`group hover:shadow-lg transition-all duration-300 overflow-hidden ${
        isFree 
          ? 'border-green-200 bg-gradient-to-br from-green-50 to-white hover:border-green-300' 
          : 'border-amber-200 bg-gradient-to-br from-amber-50 to-white hover:border-amber-300'
      }`} 
      data-testid={`tryout-card-${to.id}`}
    >
      {/* Color indicator bar */}
      <div className={`h-1.5 ${isFree ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gradient-to-r from-amber-400 to-amber-500'}`} />
      
      <CardHeader className="p-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-white">{to.category}</Badge>
          <Badge className={`text-[10px] px-1.5 py-0.5 ${
            isFree 
              ? 'bg-green-500 text-white hover:bg-green-500' 
              : 'bg-amber-500 text-white hover:bg-amber-500'
          }`}>
            {isFree ? 'GRATIS' : formatPrice(to.price)}
          </Badge>
        </div>
        <CardTitle className={`text-sm mt-1.5 line-clamp-1 ${
          isFree ? 'group-hover:text-green-600' : 'group-hover:text-amber-600'
        } transition-colors`}>
          {to.title}
        </CardTitle>
        <CardDescription className="text-xs line-clamp-1 mt-0.5">{to.description}</CardDescription>
      </CardHeader>
      
      <CardContent className="p-3 pt-0 pb-2">
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${isFree ? 'bg-green-100' : 'bg-amber-100'}`}>
            <FileText className={`w-3 h-3 ${isFree ? 'text-green-600' : 'text-amber-600'}`} />
            <span>{to.question_count} soal</span>
          </div>
          <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${isFree ? 'bg-green-100' : 'bg-amber-100'}`}>
            <Clock className={`w-3 h-3 ${isFree ? 'text-green-600' : 'text-amber-600'}`} />
            <span>{to.duration_minutes} mnt</span>
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-3 pt-0">
        <Button 
          size="sm"
          onClick={handleStartTryout}
          disabled={checking}
          className={`w-full h-7 text-xs ${
            isFree 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-amber-500 hover:bg-amber-600'
          } shadow-sm`}
        >
          {checking ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>
              {!isFree && !user && <Lock className="w-3 h-3 mr-1" />}
              {user ? 'Mulai' : 'Daftar'}
              <ArrowRight className="w-3 h-3 ml-1" />
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
